import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile
} from 'firebase/auth';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import zxcvbn from 'zxcvbn';
import { auth } from '../config/firebase';

const AuthPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(null);

  // Check if we received state from navigation (e.g., from HomePage)
  useEffect(() => {
    if (location.state?.isSignUp) {
      setIsSignUp(true);
    }
    if (location.state?.email) {
      setEmail(location.state.email);
    }
  }, [location.state]);

  const handlePasswordChange = (value) => {
    setPassword(value);
    if (isSignUp && value.length > 0) {
      // Calculate password strength using zxcvbn
      // Pass user inputs to avoid common patterns
      const userInputs = [email, firstName, lastName].filter(Boolean);
      const result = zxcvbn(value, userInputs);
      setPasswordStrength(result);
    } else {
      setPasswordStrength(null);
    }
  };

  const handleAuth = async () => {
    setError('');
    setLoading(true);

    try {
      if (isSignUp) {
        // Validation for sign-up
        if (!firstName.trim() || !lastName.trim()) {
          setError('Please enter your first and last name');
          setLoading(false);
          return;
        }

        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }

        // Check password strength using zxcvbn
        const userInputs = [email, firstName, lastName].filter(Boolean);
        const strengthResult = zxcvbn(password, userInputs);

        // Require at least a score of 2 (fair strength)
        if (strengthResult.score < 2) {
          const feedback = strengthResult.feedback.warning || 'Password is too weak';
          const suggestions = strengthResult.feedback.suggestions.length > 0
            ? ` ${strengthResult.feedback.suggestions[0]}`
            : '';
          setError(`${feedback}.${suggestions}`);
          setLoading(false);
          return;
        }

        // Create user account
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        // Update user profile with display name
        await updateProfile(userCredential.user, {
          displayName: `${firstName.trim()} ${lastName.trim()}`
        });

        // Send verification email
        try {
          await sendEmailVerification(userCredential.user);
          // User stays signed in and will be redirected to EmailVerificationPrompt by MainApp
          // The onAuthStateChanged in MainApp will detect the new user and show the verification prompt
        } catch (emailErr) {
          // If email send fails, still allow user to proceed to verification page
          // They can resend from there
          console.error('Failed to send verification email:', emailErr);
        }
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        // User will be redirected to verification prompt by MainApp if not verified
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAuth();
    }
  };

  return (
    <div className="min-h-screen bg-nova-bg flex items-center justify-center p-4">
      <div className="bg-gradient-card rounded-2xl shadow-2xl p-8 w-full max-w-md border border-nova-green/20">
        <div className="text-center mb-8">
          <img
            src="/logo.svg"
            alt="NovaSage Logo"
            className="w-32 h-32 mx-auto mb-4 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/')}
          />
          <h1 className="text-4xl font-bold text-white mb-2">Welcome to NovaSage</h1>
          <p className="text-gray-400 mb-4">Real Conversations. Real Feedback. Human Intelligence.</p>

          {/* No Credit Card Badge */}
          <div className="inline-flex items-center gap-2 bg-nova-green/10 border border-nova-green/30 px-4 py-2 rounded-full">
            <svg className="w-5 h-5 text-nova-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-nova-green font-semibold text-sm">Instant access. No card. No hassle.</span>
          </div>
        </div>

        <div className="space-y-4">
          {/* Google Sign In Button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full bg-nova-bg border-2 border-nova-green/30 text-white py-3 rounded-lg font-semibold hover:bg-nova-green/10 transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span>Continue with Google</span>
          </button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-nova-green/30"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-nova-secondary text-gray-400">Or continue with email</span>
            </div>
          </div>

          {/* First Name Input - Only show during sign up */}
          {isSignUp && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-2 bg-nova-bg border border-nova-green/30 text-white rounded-lg focus:ring-2 focus:ring-nova-green focus:border-transparent focus:shadow-glow-green-sm outline-none placeholder-gray-500 transition-shadow"
                  placeholder="John"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-2 bg-nova-bg border border-nova-green/30 text-white rounded-lg focus:ring-2 focus:ring-nova-green focus:border-transparent focus:shadow-glow-green-sm outline-none placeholder-gray-500 transition-shadow"
                  placeholder="Doe"
                  disabled={loading}
                />
              </div>
            </div>
          )}

          {/* Email Input */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full px-4 py-2 bg-nova-bg border border-nova-green/30 text-white rounded-lg focus:ring-2 focus:ring-nova-green focus:border-transparent focus:shadow-glow-green-sm outline-none placeholder-gray-500 transition-shadow"
              placeholder="your@email.com"
              disabled={loading}
            />
          </div>

          {/* Password Input */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full px-4 py-2 pr-10 bg-nova-bg border border-nova-green/30 text-white rounded-lg focus:ring-2 focus:ring-nova-green focus:border-transparent focus:shadow-glow-green-sm outline-none placeholder-gray-500 transition-shadow"
                placeholder="••••••••"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-nova-green focus:outline-none"
                disabled={loading}
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>

            {/* Password Strength Indicator - Only show during sign up */}
            {isSignUp && passwordStrength && password.length > 0 && (
              <div className="mt-2">
                <div className="flex gap-1 mb-1">
                  {[0, 1, 2, 3, 4].map((level) => (
                    <div
                      key={level}
                      className={`h-1 flex-1 rounded-full transition-colors ${level <= passwordStrength.score
                        ? passwordStrength.score === 0
                          ? 'bg-red-500'
                          : passwordStrength.score === 1
                            ? 'bg-orange-500'
                            : passwordStrength.score === 2
                              ? 'bg-yellow-500'
                              : passwordStrength.score === 3
                                ? 'bg-blue-500'
                                : 'bg-green-500'
                        : 'bg-gray-700'
                        }`}
                    />
                  ))}
                </div>
                <p className={`text-xs ${passwordStrength.score === 0 ? 'text-red-400' :
                  passwordStrength.score === 1 ? 'text-orange-400' :
                    passwordStrength.score === 2 ? 'text-yellow-400' :
                      passwordStrength.score === 3 ? 'text-blue-400' :
                        'text-green-400'
                  }`}>
                  {passwordStrength.score === 0 && 'Very Weak'}
                  {passwordStrength.score === 1 && 'Weak'}
                  {passwordStrength.score === 2 && 'Fair'}
                  {passwordStrength.score === 3 && 'Good'}
                  {passwordStrength.score === 4 && 'Strong'}
                  {passwordStrength.feedback.warning && ` - ${passwordStrength.feedback.warning}`}
                </p>
                {passwordStrength.feedback.suggestions.length > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    {passwordStrength.feedback.suggestions[0]}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Confirm Password Input - Only show during sign up */}
          {isSignUp && (
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="w-full px-4 py-2 pr-10 bg-nova-bg border border-nova-green/30 text-white rounded-lg focus:ring-2 focus:ring-nova-green focus:border-transparent focus:shadow-glow-green-sm outline-none placeholder-gray-500 transition-shadow"
                  placeholder="••••••••"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-nova-green focus:outline-none"
                  disabled={loading}
                >
                  {showConfirmPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="text-red-400 text-sm text-center bg-red-900/20 border border-red-500/30 p-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Email Sign In Button */}
          <button
            onClick={handleAuth}
            disabled={loading}
            className="w-full bg-gradient-primary text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-all transform hover:scale-105 disabled:opacity-50"
          >
            {loading ? 'Please wait...' : (isSignUp ? 'Sign Up' : 'Sign In')}
          </button>
        </div>

        {/* Toggle Sign Up/Sign In */}
        <div className="mt-6 text-center">
          <button
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-nova-green hover:text-nova-green/80 text-sm font-medium"
            disabled={loading}
          >
            {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;